import { register } from '!/repository'
import { api } from '@setplex/tria-api'
import { combine, createEvent, sample, split } from 'effector'
import { model as showsEntityModel } from '~/entities/shows'
import {
  LibraryTabs,
  getLibraryTabFromQuery,
  libraryTabsNavigationFeature,
} from '~/features/library/tabs-navigation'
import { model as guest } from '~/features/subscriber/guest'
import { aye } from '~/shared/helpers'
import { libraryFavoritesWidget } from '~/widgets/library/favorites'
import { libraryPurchasedWidget } from '~/widgets/library/purchased'
import { $libraryTranslations } from './translations'

export const initLibraryPage = createEvent<{ query: URLSearchParams }>()
export const leftLibraryPage = createEvent<{ query: URLSearchParams }>()

sample({
  clock: initLibraryPage,
  fn: (searchParams) => getLibraryTabFromQuery(searchParams.query),
  target: libraryTabsNavigationFeature.selectLibraryActiveTab,
})

//First page loading, when content isn't inited
sample({
  clock: initLibraryPage,
  source: {
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
  },
  filter: ({ isFavoritesContentInited, isPurchasedContentInited }) =>
    !isFavoritesContentInited && !isPurchasedContentInited,
  target: [
    libraryFavoritesWidget.loadFavorites,
    libraryPurchasedWidget.loadPurchased,
  ],
})

//Switch to favorite tab and update favorite content
sample({
  clock: initLibraryPage,
  source: {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
  },
  filter: ({ isFavoritesContentInited, libraryActiveTab }) =>
    isFavoritesContentInited && libraryActiveTab === LibraryTabs.FAVORITES,
  target: libraryFavoritesWidget.loadFavorites,
})

//Switch to purchased tab and update purchased content
sample({
  clock: initLibraryPage,
  source: {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
  },
  filter: ({ isPurchasedContentInited, libraryActiveTab }) =>
    isPurchasedContentInited && libraryActiveTab === LibraryTabs.PURCHASED,
  target: libraryPurchasedWidget.loadPurchased,
})

//Update purchased tab content, when tv channel payment was successful on favorites tab
sample({
  clock: api.purchases.initializePurchaseProcessFx.done,
  source: {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
  },
  filter: ({ libraryActiveTab }) => libraryActiveTab === LibraryTabs.FAVORITES,
  target: libraryPurchasedWidget.loadPurchased,
})

const $isFavoritesEmptyAndPurchasedExists = combine(
  {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
    isAnyPurchased: libraryPurchasedWidget.$isAnyPurchased,
    isAnyFavorites: libraryFavoritesWidget.$isAnyFavorites,
  },
  ({
    libraryActiveTab,
    isFavoritesContentInited,
    isPurchasedContentInited,
    isAnyPurchased,
    isAnyFavorites,
  }) =>
    libraryActiveTab === LibraryTabs.FAVORITES &&
    isFavoritesContentInited &&
    isPurchasedContentInited &&
    !isAnyFavorites &&
    isAnyPurchased
)

const $isPurchasedEmptyAndFavoritesExists = combine(
  {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
    isAnyPurchased: libraryPurchasedWidget.$isAnyPurchased,
    isAnyFavorites: libraryFavoritesWidget.$isAnyFavorites,
  },
  ({
    libraryActiveTab,
    isFavoritesContentInited,
    isPurchasedContentInited,
    isAnyPurchased,
    isAnyFavorites,
  }) =>
    libraryActiveTab === LibraryTabs.PURCHASED &&
    isFavoritesContentInited &&
    isPurchasedContentInited &&
    !isAnyPurchased &&
    isAnyFavorites
)

sample({
  clock: [
    libraryPurchasedWidget.$purchasedContent,
    libraryFavoritesWidget.$favoritesContent,
  ],
  source: {
    isFavoritesEmptyAndPurchasedExists: $isFavoritesEmptyAndPurchasedExists,
    isPurchasedEmptyAndFavoritesExists: $isPurchasedEmptyAndFavoritesExists,
    isAnyFavoritesFxPending: libraryFavoritesWidget.$isAnyFavoritesFxPending,
    isAnyPurchasedFxPending: libraryPurchasedWidget.$isAnyPurchasedFxPending,
  },
  filter: ({
    isFavoritesEmptyAndPurchasedExists,
    isPurchasedEmptyAndFavoritesExists,
    isAnyFavoritesFxPending,
    isAnyPurchasedFxPending,
  }) =>
    !isAnyFavoritesFxPending &&
    !isAnyPurchasedFxPending &&
    (isFavoritesEmptyAndPurchasedExists || isPurchasedEmptyAndFavoritesExists),
  fn: ({ isFavoritesEmptyAndPurchasedExists }) =>
    isFavoritesEmptyAndPurchasedExists
      ? LibraryTabs.PURCHASED
      : LibraryTabs.FAVORITES,
  target: libraryTabsNavigationFeature.selectLibraryActiveTab,
})

export const $isAnyFavoritesOrAnyPurchased = combine(
  libraryFavoritesWidget.$isAnyFavorites,
  libraryPurchasedWidget.$isAnyPurchased,
  (isAnyFavorites, isAnyPurchased) => isAnyFavorites || isAnyPurchased
)

const $isPurchasedLiveEventsAndPathname = combine(
  libraryTabsNavigationFeature.$libraryActiveTab,
  libraryPurchasedWidget.$purchasedContent,
  (libraryActiveTab, purchasedContent) =>
    libraryActiveTab === LibraryTabs.PURCHASED &&
    Boolean(purchasedContent.liveEvents?.content?.length)
)

export const $tvChannels = combine(
  {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    favoritesContent: libraryFavoritesWidget.$favoritesContent,
    purchasedContent: libraryPurchasedWidget.$purchasedContent,
  },
  ({ libraryActiveTab, favoritesContent, purchasedContent }) =>
    libraryActiveTab === LibraryTabs.FAVORITES
      ? favoritesContent.tvChannels
      : purchasedContent.tvChannels
)

export const $vods = combine(
  {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    favoritesContent: libraryFavoritesWidget.$favoritesContent,
    purchasedContent: libraryPurchasedWidget.$purchasedContent,
  },
  ({ libraryActiveTab, favoritesContent, purchasedContent }) =>
    libraryActiveTab === LibraryTabs.FAVORITES
      ? favoritesContent.vods
      : purchasedContent.vods
)

export const $tvShows = combine(
  {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    favoritesContent: libraryFavoritesWidget.$favoritesContent,
    purchasedContent: libraryPurchasedWidget.$purchasedContent,
  },
  ({ libraryActiveTab, favoritesContent, purchasedContent }) =>
    libraryActiveTab === LibraryTabs.FAVORITES
      ? favoritesContent.tvShows
      : purchasedContent.tvShows
)

export const $liveEvents = combine(
  libraryTabsNavigationFeature.$libraryActiveTab,
  libraryPurchasedWidget.$purchasedContent,
  (libraryActiveTab, purchasedContent) =>
    libraryActiveTab === LibraryTabs.PURCHASED
      ? purchasedContent.liveEvents
      : []
)

export const $isFavoriteTab = combine(
  libraryTabsNavigationFeature.$libraryActiveTab,
  (libraryActiveTab) => libraryActiveTab === LibraryTabs.FAVORITES
)
export const $isPurchasedTab = combine(
  libraryTabsNavigationFeature.$libraryActiveTab,
  (libraryActiveTab) => libraryActiveTab === LibraryTabs.PURCHASED
)

const seeAllLibraryTvClick = createEvent()
const seeAllLibraryMovieClick = createEvent()
const seeAllLibraryTvShowsClick = createEvent()
const seeAllLibraryLiveEventsClick = createEvent()

split({
  clock: seeAllLibraryTvClick,
  source: libraryTabsNavigationFeature.$libraryActiveTab,
  match: {
    favorites: (libraryActiveTab) => libraryActiveTab === LibraryTabs.FAVORITES,
    purchased: (libraryActiveTab) => libraryActiveTab === LibraryTabs.PURCHASED,
  },
  cases: {
    favorites: libraryFavoritesWidget.seeAllLibraryFavoritesTvClick,
    purchased: libraryPurchasedWidget.seeAllLibraryPurchasedTvClick,
  },
})

split({
  clock: seeAllLibraryMovieClick,
  source: libraryTabsNavigationFeature.$libraryActiveTab,
  match: {
    favorites: (libraryActiveTab) => libraryActiveTab === LibraryTabs.FAVORITES,
    purchased: (libraryActiveTab) => libraryActiveTab === LibraryTabs.PURCHASED,
  },
  cases: {
    favorites: libraryFavoritesWidget.seeAllLibraryFavoritesMovieClick,
    purchased: libraryPurchasedWidget.seeAllLibraryPurchasedMovieClick,
  },
})

split({
  clock: seeAllLibraryTvShowsClick,
  source: libraryTabsNavigationFeature.$libraryActiveTab,
  match: {
    favorites: (libraryActiveTab) => libraryActiveTab === LibraryTabs.FAVORITES,
    purchased: (libraryActiveTab) => libraryActiveTab === LibraryTabs.PURCHASED,
  },
  cases: {
    favorites: libraryFavoritesWidget.seeAllLibraryFavoritesTvShowsClick,
    purchased: libraryPurchasedWidget.seeAllLibraryPurchasedTvShowsClick,
  },
})

sample({
  clock: seeAllLibraryLiveEventsClick,
  target: libraryPurchasedWidget.seeAllLibraryPurchasedLiveEventsClick,
})

sample({
  clock: [leftLibraryPage, api.session.signOutFx.done],
  source: {
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
  },
  filter: ({ isFavoritesContentInited, isPurchasedContentInited }) =>
    isFavoritesContentInited || isPurchasedContentInited,
  target: [
    libraryFavoritesWidget.resetFavorites,
    libraryPurchasedWidget.resetPurchased,
  ],
})
// reset store on session expiring and when guest mode
sample({
  clock: guest.$guest,
  source: {
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
  },
  filter: ({ isFavoritesContentInited, isPurchasedContentInited }, isGuest) =>
    (isFavoritesContentInited || isPurchasedContentInited) && aye(isGuest),
  target: [
    libraryFavoritesWidget.resetFavorites,
    libraryPurchasedWidget.resetPurchased,
  ],
})

libraryTabsNavigationFeature.$libraryActiveTab.reset(leftLibraryPage)

register($libraryTranslations, '$libraryTranslations')
register(libraryTabsNavigationFeature.$libraryTabNames, '$libraryTabNames')
register(libraryPurchasedWidget.$isAnyPurchased, '$isAnyPurchased')
register(libraryFavoritesWidget.$isAnyFavorites, '$isAnyFavorites')
register($isAnyFavoritesOrAnyPurchased, '$isAnyFavoritesOrAnyPurchased')
register($tvChannels, '$libraryTvChannels')
register($vods, '$libraryVods')
register($tvShows, '$libraryTvShows')
register($liveEvents, '$libraryLiveEvents')
register(seeAllLibraryTvClick, 'seeAllLibraryTvClick')
register(seeAllLibraryMovieClick, 'seeAllLibraryMovieClick')
register(seeAllLibraryTvShowsClick, 'seeAllLibraryTvShowsClick')
register(seeAllLibraryLiveEventsClick, 'seeAllLibraryLiveEventsClick')
register($isPurchasedLiveEventsAndPathname, '$isPurchasedLiveEventsAndPathname')
register(showsEntityModel.updateTvShow, 'updateTvShow')
register(guest.$guest, '$guest')
register($isFavoriteTab, '$isFavoriteTab')
register($isPurchasedTab, '$isPurchasedTab')
register(libraryTabsNavigationFeature.$libraryActiveTab, '$libraryActiveTab')
